import { useTranslation } from "react-i18next";
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddCommentOutlined,
  ArrowBack,
  ArrowForward,
  BugReportOutlined,
  Close,
} from "@mui/icons-material";

/** Mapit short logo (the eagle mark), served from `public/`. */
const AVATAR_SRC = "/logo192.png";

/** Icon button with a tooltip; the span keeps the tooltip on a disabled one. */
const HeaderButton = ({ label, children, disabled = false, ...props }) => (
  <Tooltip title={label}>
    <span>
      <IconButton aria-label={label} disabled={disabled} {...props}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

/**
 * Panel header (plan §3.4): [Back] avatar + status dot, title, [debug] [+] ✕.
 * Optional buttons render only when their handler is passed: Back and "+" in
 * the conversation view, the debug toggle for AI developers (T13).
 *
 * @param {object} props
 * @param {string} props.titleId id of the title, the panel's `aria-labelledby`
 * @param {() => void} props.onClose
 * @param {boolean} [props.available] status dot: green, or a grey ring after
 *   852; named «متاح الآن» / «غير متاح حاليًا» for screen readers
 * @param {() => void} [props.onBack]
 * @param {() => void} [props.onNewConversation]
 * @param {boolean} [props.newConversationDisabled] e.g. while an answer streams
 * @param {() => void} [props.onDebugToggle]
 * @param {boolean} [props.debugOn]
 */
export default function PanelHeader({
  titleId,
  onClose,
  available = true,
  onBack,
  onNewConversation,
  newConversationDisabled = false,
  onDebugToggle,
  debugOn = false,
}) {
  const { t } = useTranslation("aiSupport");
  const theme = useTheme();
  const statusLabel = available
    ? t("panel.statusAvailable")
    : t("panel.statusUnavailable");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        minHeight: 64,
        borderBottom: 1,
        borderColor: "divider",
        flexShrink: 0,
      }}
    >
      {onBack && (
        <HeaderButton label={t("panel.back")} onClick={onBack}>
          {/* Icons don't mirror themselves: "back" points right in RTL */}
          {theme.direction === "rtl" ? <ArrowForward /> : <ArrowBack />}
        </HeaderButton>
      )}
      {/* The dot is named for screen readers and differs in shape too (a
          filled green dot, a hollow grey ring), not only in colour */}
      <Tooltip title={statusLabel}>
        <Badge
          role="img"
          aria-label={statusLabel}
          overlap="circular"
          variant="dot"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{
            flexShrink: 0,
            "& .MuiBadge-badge": {
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: available ? "success.main" : "background.paper",
              border: available ? 0 : 2,
              borderColor: "grey.600",
              boxShadow: (th) => `0 0 0 2px ${th.palette.background.paper}`,
            },
          }}
        >
          <Avatar
            src={AVATAR_SRC}
            alt=""
            sx={{
              width: 36,
              height: 36,
              bgcolor: "common.white",
              border: 1,
              borderColor: "divider",
            }}
          />
        </Badge>
      </Tooltip>
      <Typography
        id={titleId}
        component="h2"
        variant="subtitle1"
        noWrap
        sx={{ flexGrow: 1, minWidth: 0, fontWeight: 600 }}
      >
        {t("botName")}
      </Typography>
      {onDebugToggle && (
        <HeaderButton
          label={t("panel.debugToggle")}
          aria-pressed={debugOn}
          color={debugOn ? "primary" : "default"}
          onClick={onDebugToggle}
        >
          <BugReportOutlined />
        </HeaderButton>
      )}
      {onNewConversation && (
        <HeaderButton
          label={t("panel.newConversation")}
          disabled={newConversationDisabled}
          onClick={onNewConversation}
        >
          <AddCommentOutlined />
        </HeaderButton>
      )}
      <HeaderButton label={t("panel.close")} onClick={onClose}>
        <Close />
      </HeaderButton>
    </Box>
  );
}
